/**
 * About / changelog modal, reachable from the version chip. Shows the version,
 * a short changelog, the privacy statement, and the calibration disclaimer.
 *
 * Also exports a small, dependency-free accessible-modal helper (openModal)
 * reused by the help overlay: role=dialog + aria-modal + aria-labelledby, a
 * focus trap while open, Escape-to-close, and focus restoration to the element
 * that opened it. Honors prefers-reduced-motion via CSS class hooks only.
 */
import { APP_NAME, APP_VERSION } from "../core/types";
import { el } from "./app";

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

export interface ModalHandle {
  root: HTMLElement;
  close(): void;
}

export interface ModalOptions {
  titleId: string;
  labelledBy?: string;
  /** Extra class on the .modal card for per-modal styling. */
  cardClass?: string;
  /** Element to restore focus to on close (defaults to the active element). */
  returnFocusTo?: HTMLElement | null;
  onClose?: () => void;
}

/**
 * Mount an accessible modal into <body>. `build` receives a `close` callback so
 * content can dismiss the dialog (e.g. a Close button). Returns a handle.
 */
export function openModal(
  build: (close: () => void) => { title: Node; body: Node },
  opts: ModalOptions,
): ModalHandle {
  const opener =
    opts.returnFocusTo ??
    (document.activeElement instanceof HTMLElement ? document.activeElement : null);

  const overlay = el("div", { class: "modal-backdrop" });
  const card = el("div", {
    class: "modal" + (opts.cardClass ? " " + opts.cardClass : ""),
    attrs: {
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": opts.labelledBy ?? opts.titleId,
    },
  });

  const close = (): void => {
    if (!overlay.isConnected) return;
    document.removeEventListener("keydown", onKeydown, true);
    overlay.remove();
    opts.onClose?.();
    if (opener && opener.isConnected) opener.focus();
  };

  const closeBtn = el("button", {
    class: "modal-close",
    text: "×",
    attrs: { type: "button", "aria-label": "Close" },
    onClick: () => close(),
  });

  const { title, body } = build(close);
  const header = el("div", { class: "modal-head" }, [title, closeBtn]);
  const content = el("div", { class: "modal-body" }, [body]);
  card.append(header, content);
  overlay.append(card);

  overlay.addEventListener("mousedown", (e) => {
    if (e.target === overlay) close();
  });

  const onKeydown = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== "Tab") return;
    const focusable = Array.from(card.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (n) => n.offsetParent !== null || n === document.activeElement,
    );
    if (!focusable.length) {
      e.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey && (active === first || !card.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };
  document.addEventListener("keydown", onKeydown, true);

  document.body.append(overlay);
  // Move focus into the dialog.
  const firstFocusable = card.querySelector<HTMLElement>(FOCUSABLE);
  (firstFocusable ?? card).focus();

  return { root: overlay, close };
}

interface ChangelogEntry {
  version: string;
  notes: string[];
}

const CHANGELOG: ChangelogEntry[] = [
  {
    version: "3.2.2.0",
    notes: [
      "The Scan angle field now has a live direction swatch showing the hatch lines at that angle, with arrows for bi-directional (back-and-forth) vs uni-directional (one-way with flyback) fill.",
    ],
  },
  {
    version: "3.2.1.0",
    notes: [
      "Reworked the layer cut-parameter editor into a single parameter table (like LightBurn): click a layer to select it and the table below edits that layer's settings.",
      "Each layer row now shows its speed/power at a glance; no more overlapping expanders.",
    ],
  },
  {
    version: "3.2.0.0",
    notes: [
      "Full per-layer MOPA controls: Power, Speed, Frequency, Q-pulse, Line interval, Scan angle, Passes, Cross-hatch, Bi-directional, and Fill all shapes at once vs individually — all embedded in the LightBurn export.",
      "Layer parameter panels now open one at a time (accordion) and open cleanly.",
      "Photo-mode color rows now show their parameter editor too.",
    ],
  },
  {
    version: "3.1.0.0",
    notes: [
      "New Heart dot shape, plus a \"Flip shape on offset rows\" option so hearts alternate point-down / point-up and nest together.",
      "Hex packing: one toggle sets the row spacing to a true honeycomb where every dot is equidistant from its six neighbours.",
      "Dot stretch: stretch any dot vertically — a diamond becomes a tall rhombus, a circle an oval, and so on.",
      "Every numeric field now has −/+ steppers for fine tuning.",
      "Fixed a layout glitch where the workspace could collapse to half the window height on some browsers.",
    ],
  },
  {
    version: "3.0.1.0",
    notes: [
      "Adjacent color layers now share one dot grid, so shapes that touch tile cleanly at the seam instead of doubling up or leaving gaps.",
      "Fixed the Diamond dot — it's now a true diamond (points up/down/left/right), not a rotated square.",
      "Two new dot shapes: Octagon and Star.",
      "Widescreen editor now spans the full width, with the controls docked on the right.",
    ],
  },
  {
    version: "3.0.0.1",
    notes: [
      "Offline support now stays strictly within the app — other pages on this domain are never affected.",
    ],
  },
  {
    version: "3.0.0.0",
    notes: [
      "Rebuilt for every screen — phones through 4K, portrait and landscape, with pinch-zoom and pan on the canvas.",
      "Plain-language controls with the technical detail kept in tooltips.",
      "Material presets front and center, plus one-click sample files to try instantly.",
      "Before/after compare, an mm ⇄ inch toggle, and comma-or-period number entry.",
      "One-click Export all bundles the SVG, palette CSV, LightBurn file, and report into a single download.",
      "Faster, calmer color separation with an editable calibration swatch library.",
    ],
  },
];

export function openAboutModal(returnFocusTo?: HTMLElement | null): ModalHandle {
  return openModal(
    () => {
      const title = el("h2", { class: "modal-title", attrs: { id: "about-title" } }, [
        APP_NAME,
        " ",
        el("span", { class: "modal-version", text: "v" + APP_VERSION }),
      ]);

      const body = el("div", { class: "about-body" });

      const privacy = el("section", { class: "about-privacy" }, [
        el("h3", { text: "Your files stay with you" }),
        el("p", {
          text:
            "Everything runs inside your browser. Your files are never uploaded, there are no accounts, and nothing is tracked. Disconnect from the network and it still works.",
        }),
      ]);

      const calib = el("section", { class: "about-calibration" }, [
        el("h3", { text: "Colors need calibration" }),
        el("p", {
          text:
            "MOPA marking colors depend on your exact machine, lens, focus, and material. Presets are starting points only — burn a test grid and measure the results before relying on any value.",
        }),
      ]);

      const changelog = el("section", { class: "about-changelog" }, [
        el("h3", { text: "What's new" }),
      ]);
      for (const entry of CHANGELOG) {
        changelog.append(el("h4", { text: "Version " + entry.version }));
        const ul = el("ul");
        for (const note of entry.notes) ul.append(el("li", { text: note }));
        changelog.append(ul);
      }

      body.append(privacy, calib, changelog);
      return { title, body };
    },
    { titleId: "about-title", labelledBy: "about-title", cardClass: "about-card", returnFocusTo },
  );
}
